import { useMemo } from 'react';

import { useQuery } from '@apollo/client';

import { useProgram } from 'bundles/enterprise-admin-ai-program-builder/hooks/useProgram';
import programSuggestionsQuery from 'bundles/enterprise-admin-ai-program-builder/queries/QueryProgramSuggestions.graphql';
import type {
  QueryProgramSuggestionsQuery,
  QueryProgramSuggestionsQueryVariables,
} from 'bundles/enterprise-admin-ai-program-builder/queries/__generated__/QueryProgramSuggestions';
import type {
  ContentSuggestion,
  RecommendationItemType,
  RecommendationType,
} from 'bundles/enterprise-admin-ai-program-builder/types';

const courseTypeMap = {
  Course_RhymeProjectMetadata: 'GUIDED_PROJECT',
  Course_ProjectMetadata: 'PROJECT',
  Course_ShortFormContentMetadata: 'COURSE',
  Course_StandardCourseMetadata: 'COURSE',
} as const;
const getCourseType = (typeName: keyof typeof courseTypeMap) => {
  return courseTypeMap[typeName];
};

const specializationTypeMap = {
  NORMAL_S12N: 'SPECIALIZATION',
  GOOGLE_CERTIFICATE_S12N: 'PROFESSIONAL_CERTIFICATE',
  PROFESSIONAL_CERTIFICATE_S12N: 'PROFESSIONAL_CERTIFICATE',
  EXTERNAL_CERTIFICATE_S12N: 'SPECIALIZATION',
} as const;
const getSpecializationType = (typeName: keyof typeof specializationTypeMap) => {
  return specializationTypeMap[typeName];
};

export const useProgramSuggestionsQuery = (skip: boolean, recommendationType: RecommendationType) => {
  const program = useProgram();
  const { data, refetch, error, loading } = useQuery<
    QueryProgramSuggestionsQuery,
    QueryProgramSuggestionsQueryVariables
  >(programSuggestionsQuery, {
    context: { clientName: 'gatewayGql' },
    skip: !program?.id || skip,
    variables: {
      programId: program?.id ?? '',
    },
    fetchPolicy: 'network-only',
  });
  const suggestions = useMemo(() => {
    const collectionsSuggestions: ContentSuggestion[] =
      data?.EnterpriseProgram?.queryProgramSuggestions?.collections
        ?.map((collection) => {
          if (collection.__typename === 'EnterpriseProgram_CollectionSuggestions') {
            return {
              id: collection.name,
              title: collection.name,
              description: collection.description,
              learningItems: collection.collectionItems.map((item) => {
                let partners: { id: string; name: string }[] = [];
                let preview = '';
                if (item.__typename === 'Clip_CourseItem' && item.clipPartners) {
                  partners = item.clipPartners;
                  preview = `/videos/${item.course.slug}/${item.id.split('~')[1]}`;
                } else if (item.__typename === 'Course_Course') {
                  partners = item.partners;
                  preview = `/learn/${item.slug}`;
                } else if (item.__typename === 'Specialization_Specialization') {
                  partners = item.partners;
                  preview = item.urlPath;
                }
                let productType: RecommendationItemType = (
                  {
                    Course_Course: 'COURSE',
                    Specialization_Specialization: 'SPECIALIZATION',
                    Clip_CourseItem: 'VIDEO',
                  } as const
                )[item.__typename ?? 'Course_Course'];
                if (item.__typename === 'Course_Course') {
                  productType = getCourseType(item.courseTypeMetadata.metadata.__typename);
                }
                if (item.__typename === 'Specialization_Specialization') {
                  productType = getSpecializationType(item.productVariant);
                }
                return {
                  id: item.id,
                  name: item.name,
                  productType,

                  partners,
                  preview,
                };
              }),
              type: (
                {
                  EnterpriseProgram_CollectionSuggestions: 'COLLECTIONS',
                } as const
              )[collection.__typename ?? 'EnterpriseProgram_CollectionSuggestions'],
              skills: [],
            };
          }
          if (collection.__typename === 'Collection_Collection') {
            return {
              id: collection.collectionAttributes.collectionId,
              referenceCollectionId: collection.collectionAttributes.collectionId,
              title: collection.description,
              description: '',
              skills: [],
              learningItems:
                collection.entities?.elements.reduce((all, entity) => {
                  if (
                    entity?.__typename === 'ProductCard_ProductCard' &&
                    entity.canonicalType === 'COURSE' &&
                    entity.productTypeAttributes.__typename === 'ProductCard_Course'
                  ) {
                    const productType: RecommendationItemType = getCourseType(
                      entity.productTypeAttributes.canonical.courseTypeMetadata?.metadata?.__typename
                    );
                    return [
                      ...all,
                      {
                        id: entity.id,
                        name: entity.productTypeAttributes.canonical.name,
                        productType,
                        partners: entity.productTypeAttributes.canonical.partners,
                        preview: `/learn/${entity.productTypeAttributes.canonical.slug}`,
                      },
                    ];
                  }
                  return all;
                }, [] as ContentSuggestion['learningItems']) ?? [],
              type: 'COLLECTIONS' as const,
            };
          }
          return undefined;
        })
        .filter((suggestion, index, all) => all.findIndex((s) => s?.id === suggestion?.id) === index)
        // not filtering both conditions in one go because typescript does not remove undefined from the type
        .filter((suggestion) => suggestion !== undefined) ?? [];

    const learningPathSuggestions = data?.EnterpriseProgram?.queryProgramSuggestions?.learningPathSuggestions?.map(
      (_) => {
        return {
          id: _.name,
          title: _.name,
          description: _.description,
          skills: _.skills.map((skill) => skill.id),
          learningObjectives: _.learningObjectives.slice(0, 4),
          learningItems: _.courses.map((course) => {
            const productType: RecommendationItemType = getCourseType(course.courseTypeMetadata?.metadata?.__typename);
            return {
              id: course.id,
              name: course.name,
              productType,
              partners: course.partners,
              preview: `/learn/${course.slug}`,
            };
          }),
          type: 'LEARNING_PATHS' as const,
        };
      }
    );

    return (
      {
        COLLECTIONS: collectionsSuggestions,
        LEARNING_PATHS: learningPathSuggestions,
      } as const
    )[recommendationType];
  }, [
    data?.EnterpriseProgram?.queryProgramSuggestions?.collections,
    data?.EnterpriseProgram?.queryProgramSuggestions?.learningPathSuggestions,
    recommendationType,
  ]);

  return {
    suggestions,
    refetch,
    error,
    loading,
  };
};
